import { SavedTravelerListProps } from '@/src/components/common/forms/savedTravelerList'
import { DocumentDetails, DocumentType, SavedTraveler, Traveler } from '@/src/types/itinerary'
import { isoStringToDateObject } from '@/src/utils/itinerary/dateTransformer'
import { genderToTitleMap } from '@/src/utils/itinerary/genderToTitleMap'
import * as TravelerDocumentUtils from '@/src/utils/itinerary/travelerDocumentToInputsSegregater'
import { useCallback, useEffect, useRef } from 'react'

export const useSavedTraveler = ({
    travelers,
    setTravelers,
    validateTraveler,
    contactValidation,
    contactDetails,
    isNationalityRequiredToRenderInUserPersonalDetailsSection,
    isDateOfBirthRequired,
    isBirthCountryRequired
}: {
    travelers: Traveler[]
    setTravelers: (travelers: Traveler[]) => void
    validateTraveler: (index: number) => boolean
    contactValidation: any
    contactDetails: any
    isNationalityRequiredToRenderInUserPersonalDetailsSection: boolean
    isDateOfBirthRequired: boolean
    isBirthCountryRequired: boolean
}) => {
    // We use a ref to always access the latest contactDetails inside event handlers.
    // Why? Because onSavedTravelerSelect is passed into a memoized child that doesn't
    // re-render when contactDetails changes. That means if we captured contactDetails
    // directly in the callback, it would close over a stale value.
    // By syncing contactRef.current in a useEffect, we ensure that any handler,
    // regardless of when it was created, always reads the most up-to-date state.
    const contactRef = useRef(contactDetails)

    useEffect(() => {
        contactRef.current = contactDetails
    }, [contactDetails])

    const onSavedTravelerSelect: SavedTravelerListProps['onSavedTravelerSelect'] = useCallback(
        (idx: number, savedTraveler: SavedTraveler) => {
            console.log(contactDetails, 'to')
            const targetTraveler = travelers[idx]
            if (!targetTraveler) return

            targetTraveler.firstName = savedTraveler?.personalDetails?.firstName || ''
            targetTraveler.lastName = savedTraveler?.personalDetails?.lastName || ''
            targetTraveler.title =
                savedTraveler?.personalDetails?.title ||
                genderToTitleMap(savedTraveler?.personalDetails?.gender || '', targetTraveler.paxType)
            targetTraveler.id = savedTraveler?.personalDetails?.id || null

            // Handle nationality if required
            if (isNationalityRequiredToRenderInUserPersonalDetailsSection) {
                targetTraveler.nationality = savedTraveler?.personalDetails?.nationality || ''
            }
            // Handle birth country if required
            if (isBirthCountryRequired) {
                targetTraveler.birthCountry = savedTraveler?.personalDetails?.birthCountry || ''
            }

            // Handle date of birth if required
            if (isDateOfBirthRequired) {
                targetTraveler.dateOfBirth = isoStringToDateObject(
                    savedTraveler?.personalDetails?.dateOfBirth || ''
                ) || {
                    day: '',
                    month: '',
                    year: ''
                }
            }

            // Handle document details
            savedTraveler?.docDetails?.forEach(doc => {
                const documentType = doc.docType
                const documentInputs = Object.keys(
                    TravelerDocumentUtils.travelerDocumentToInputsSegregate(documentType as DocumentType)
                ) as (keyof DocumentDetails)[]
                if (documentType) {
                    documentInputs.forEach(input => {
                        if (Object.hasOwn(targetTraveler.documentDetails, documentType) && doc[input]) {
                            if (input === 'docType') {
                                targetTraveler.documentDetails[documentType][input] = documentType
                            } else if (input === 'expiryDate' || input === 'issueDate') {
                                targetTraveler.documentDetails[documentType][input] = isoStringToDateObject(
                                    doc[input] as string
                                ) || {
                                    day: '',
                                    month: '',
                                    year: ''
                                }
                            } else {
                                ;(targetTraveler.documentDetails[documentType] as any)[input] = doc[input] as string
                            }
                        }
                    })
                }
            })

            setTravelers(travelers.map((traveler, index) => (index === idx ? targetTraveler : traveler)))

            // Validate the traveler
            validateTraveler(idx)

            // Validate the contact
            contactValidation.validateAll({
                email: contactRef.current.email,
                mobile: {
                    mobile: contactRef.current.mobile,
                    mobileCountryCode: contactRef.current.mobileCountryCode
                },
                mobileCountryCode: contactRef.current.mobileCountryCode
            })
        },
        [
            travelers,
            setTravelers,
            validateTraveler,
            contactValidation,
            contactDetails,
            isNationalityRequiredToRenderInUserPersonalDetailsSection,
            isDateOfBirthRequired,
            isBirthCountryRequired
        ]
    )

    return {
        onSavedTravelerSelect
    }
}
